import { ChevronDown, Search } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/ui/input'
import { cn, fuzzyIncludes } from '@/lib/utils'

const MAX_PANEL_HEIGHT = 340
const MIN_PANEL_HEIGHT = 180
const GAP = 6

interface Country {
  iso: string
  dial: string
}

// Curated list — Slovak market first, then common EU + worldwide.
// Country names are resolved at render time via Intl.DisplayNames (localized).
const COUNTRIES: Country[] = [
  { iso: 'SK', dial: '421' },
  { iso: 'CZ', dial: '420' },
  { iso: 'AT', dial: '43' },
  { iso: 'HU', dial: '36' },
  { iso: 'PL', dial: '48' },
  { iso: 'DE', dial: '49' },
  { iso: 'UA', dial: '380' },
  { iso: 'GB', dial: '44' },
  { iso: 'IE', dial: '353' },
  { iso: 'FR', dial: '33' },
  { iso: 'IT', dial: '39' },
  { iso: 'ES', dial: '34' },
  { iso: 'PT', dial: '351' },
  { iso: 'NL', dial: '31' },
  { iso: 'BE', dial: '32' },
  { iso: 'CH', dial: '41' },
  { iso: 'SE', dial: '46' },
  { iso: 'NO', dial: '47' },
  { iso: 'DK', dial: '45' },
  { iso: 'FI', dial: '358' },
  { iso: 'RO', dial: '40' },
  { iso: 'BG', dial: '359' },
  { iso: 'HR', dial: '385' },
  { iso: 'SI', dial: '386' },
  { iso: 'RS', dial: '381' },
  { iso: 'GR', dial: '30' },
  { iso: 'LT', dial: '370' },
  { iso: 'LV', dial: '371' },
  { iso: 'EE', dial: '372' },
  { iso: 'US', dial: '1' },
  { iso: 'CA', dial: '1' },
  { iso: 'RU', dial: '7' },
  { iso: 'TR', dial: '90' },
  { iso: 'AE', dial: '971' },
  { iso: 'AU', dial: '61' },
  { iso: 'IN', dial: '91' },
  { iso: 'CN', dial: '86' }
]

const BY_DIAL_DESC = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)
const DEFAULT_ISO = 'SK'
const FALLBACK_COUNTRY: Country = { iso: 'SK', dial: '421' }

function flagEmoji(iso: string) {
  return iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
}

function countryByIso(iso: string): Country {
  return COUNTRIES.find((c) => c.iso === iso) ?? FALLBACK_COUNTRY
}

function parsePhone(value?: string): { iso: string; national: string } {
  if (!value) return { iso: DEFAULT_ISO, national: '' }
  let rest = value.trim()
  if (rest.startsWith('+')) rest = rest.slice(1)
  else if (rest.startsWith('00')) rest = rest.slice(2)
  else return { iso: DEFAULT_ISO, national: rest.replace(/\D/g, '') }
  rest = rest.replace(/\D/g, '')
  const match = BY_DIAL_DESC.find((c) => rest.startsWith(c.dial))
  if (match) return { iso: match.iso, national: rest.slice(match.dial.length) }
  return { iso: DEFAULT_ISO, national: rest }
}

/** Group digits in blocks of three for display: "902123456" -> "902 123 456". */
function groupDigits(s: string) {
  return s.replace(/\D/g, '').replace(/(\d{3})(?=\d)/g, '$1 ').trim()
}

interface PhoneInputProps {
  value?: string
  onChange?: (value: string) => void
  invalid?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
}

export function PhoneInput({
  value,
  onChange,
  invalid,
  disabled,
  placeholder = '902 123 456',
  className
}: PhoneInputProps) {
  const { t, i18n } = useTranslation('layout')
  const countryName = React.useMemo(() => {
    let dn: Intl.DisplayNames | undefined
    try {
      dn = new Intl.DisplayNames([i18n.language || 'sk'], { type: 'region' })
    } catch {
      dn = undefined
    }
    return (iso: string) => {
      try {
        return dn?.of(iso) ?? iso
      } catch {
        return iso
      }
    }
  }, [i18n.language])

  const parsed = parsePhone(value)
  const [iso, setIso] = React.useState(parsed.iso)
  const [national, setNational] = React.useState(parsed.national)
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [coords, setCoords] = React.useState<{
    top?: number
    bottom?: number
    left: number
    width: number
    maxHeight: number
  }>({ top: 0, left: 0, width: 0, maxHeight: MAX_PANEL_HEIGHT })
  const ref = React.useRef<HTMLDivElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  const reposition = React.useCallback(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const spaceBelow = window.innerHeight - r.bottom - GAP - 8
    const spaceAbove = r.top - GAP - 8
    // Open upward only when below is too cramped and above has more room.
    const openUp = spaceBelow < MIN_PANEL_HEIGHT && spaceAbove > spaceBelow
    const available = openUp ? spaceAbove : spaceBelow
    const maxHeight = Math.max(
      MIN_PANEL_HEIGHT,
      Math.min(MAX_PANEL_HEIGHT, available)
    )
    const width = Math.max(r.width, 240)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    if (openUp) {
      setCoords({
        bottom: window.innerHeight - r.top + GAP,
        left,
        width,
        maxHeight
      })
    } else {
      setCoords({ top: r.bottom + GAP, left, width, maxHeight })
    }
  }, [])

  // Re-sync from an external value change (e.g. form reset / async load).
  React.useEffect(() => {
    const next = parsePhone(value)
    const current = `+${countryByIso(iso).dial}${national}`
    if (value !== current && value !== `+${countryByIso(next.iso).dial}${next.national}`) {
      setIso(next.iso)
      setNational(next.national)
    } else if (!value && national) {
      setIso(next.iso)
      setNational('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Position synchronously before paint so the first open lands correctly
  // (otherwise the portal flashes at the stale initial coords).
  React.useLayoutEffect(() => {
    if (!open) return
    reposition()
    // Focus without scrolling — a plain autoFocus would scroll the search box
    // into view and shift the (fixed) panel away from the trigger.
    searchRef.current?.focus({ preventScroll: true })
  }, [open, reposition])

  React.useEffect(() => {
    if (!open) return
    const onScroll = () => reposition()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !ref.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('mousedown', onDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const emit = (nextIso: string, nextNational: string) => {
    const digits = nextNational.replace(/\D/g, '')
    onChange?.(digits ? `+${countryByIso(nextIso).dial}${digits}` : '')
  }

  const country = countryByIso(iso)
  const filtered = query
    ? COUNTRIES.filter(
        (c) =>
          fuzzyIncludes(countryName(c.iso), query) ||
          c.dial.includes(query.replace(/\D/g, '')) ||
          c.iso.toLowerCase().includes(query.toLowerCase())
      )
    : COUNTRIES

  return (
    <div ref={ref} className={cn('relative', className)}>
      <div
        className={cn(
          'flex h-10 w-full items-stretch overflow-hidden rounded-2xl border bg-white transition-colors focus-within:border-primary',
          invalid ? 'border-destructive' : 'border-ink/[0.16]',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className="flex shrink-0 items-center gap-1.5 border-0 border-r border-ink/[0.12] bg-transparent px-3 py-0 text-sm text-ink transition-colors hover:bg-ink/[0.03] disabled:cursor-not-allowed"
        >
          <span className="text-base leading-none">{flagEmoji(country.iso)}</span>
          <span className="font-medium">+{country.dial}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        <input
          type="tel"
          inputMode="numeric"
          disabled={disabled}
          value={groupDigits(national)}
          placeholder={placeholder}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '')
            setNational(digits)
            emit(iso, digits)
          }}
          className="h-full min-w-0 flex-1 border-0 bg-transparent px-3.5 text-sm text-ink outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
      </div>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-floating-panel=""
            style={{
              position: 'fixed',
              top: coords.top,
              bottom: coords.bottom,
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight
            }}
            className="pointer-events-auto z-[1300] flex flex-col overflow-hidden rounded-2xl border border-ink/[0.08] bg-white shadow-[0_16px_40px_rgba(4,33,37,0.16)]"
          >
            <div className="relative shrink-0 border-b border-ink/[0.06] p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={query}
              placeholder={t('phoneInput.searchCountry')}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 border-0 pl-9 shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">—</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.iso}
                  type="button"
                  onClick={() => {
                    setIso(c.iso)
                    emit(c.iso, national)
                    setOpen(false)
                    setQuery('')
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-xl border-0 bg-transparent px-3 py-2 text-left text-sm transition-colors hover:bg-ink/[0.04]',
                    c.iso === iso ? 'font-semibold text-primary' : 'text-ink'
                  )}
                >
                  <span className="text-base leading-none">
                    {flagEmoji(c.iso)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {countryName(c.iso)}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    +{c.dial}
                  </span>
                </button>
              ))
            )}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
